import React, { useState, useMemo } from 'react'
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Cell, Legend } from 'recharts'
import ChartContainer from './ChartContainer'
import ChartTooltip from './ChartTooltip'
import { CHART_COLORS, POSITIVE_COLOR, NEGATIVE_COLOR, CHART_MARGINS } from '../../utils/chartConstants'
import { transformChartDataWithSeries, isValidNumericValue, formatAxisValue } from '../../utils/chartUtils'
import { parseTargetValues, spreadTargetValues } from '../../utils/targetUtils'
import { useCubeFieldLabel } from '../../hooks/useCubeFieldLabel'
import type { ChartProps } from '../../types'

const BarChart = React.memo(function BarChart({
  data,
  chartConfig,
  displayConfig = {},
  queryObject,
  height = "100%",
  colorPalette
}: ChartProps) {
  const [hoveredLegend, setHoveredLegend] = useState<string | null>(null)
  // Use specialized hook to avoid re-renders from unrelated context changes
  const getFieldLabel = useCubeFieldLabel()

  // Determine stacking from stackType (new) or stacked (legacy)
  const stackType = displayConfig?.stackType ?? (displayConfig?.stacked ? 'normal' : 'none')
  const shouldStack = stackType !== 'none'
  const isPercentStack = stackType === 'percent'

  const safeDisplayConfig = {
    showLegend: displayConfig?.showLegend ?? true,
    showGrid: displayConfig?.showGrid ?? true,
    showTooltip: displayConfig?.showTooltip ?? true
  }

  // Extract axis format configs
  const leftYAxisFormat = displayConfig?.leftYAxisFormat
  const rightYAxisFormat = displayConfig?.rightYAxisFormat

  // Validate chartConfig - support both legacy and new formats
  // Do validation but don't early return yet (hooks must come first)
  let xAxisField: string | undefined
  let yAxisFields: string[] = []
  let seriesFields: string[] = []
  let configError: string | null = null

  if (chartConfig?.xAxis && chartConfig?.yAxis) {
    // New format
    xAxisField = Array.isArray(chartConfig.xAxis) ? chartConfig.xAxis[0] : chartConfig.xAxis
    yAxisFields = Array.isArray(chartConfig.yAxis) ? chartConfig.yAxis : [chartConfig.yAxis]
    seriesFields = chartConfig.series || []
  } else if (chartConfig?.x && chartConfig?.y) {
    // Legacy format
    xAxisField = chartConfig.x
    yAxisFields = Array.isArray(chartConfig.y) ? chartConfig.y : [chartConfig.y]
  } else {
    configError = 'Invalid or missing chart axis configuration'
  }

  if (!configError && (!xAxisField || !yAxisFields || yAxisFields.length === 0)) {
    configError = 'Missing required X-axis or Y-axis fields'
  }

  // Transform data (will be empty arrays if config is invalid)
  const { data: transformedData, seriesKeys } = useMemo(() => {
    if (configError || !data || data.length === 0 || !xAxisField) {
      return { data: [], seriesKeys: [] }
    }
    return transformChartDataWithSeries(
      data,
      xAxisField,
      yAxisFields,
      queryObject,
      seriesFields,
      getFieldLabel
    )
  }, [data, xAxisField, yAxisFields, queryObject, seriesFields, getFieldLabel, configError])

  // Dual Y-axis support: extract yAxisAssignment from chartConfig (memoized to prevent object recreation)
  const yAxisAssignment = useMemo(() =>
    chartConfig?.yAxisAssignment || {},
    [chartConfig?.yAxisAssignment]
  )

  // Build mapping from series key (label) to original field name
  const seriesKeyToField: Record<string, string> = useMemo(() => {
    const mapping: Record<string, string> = {}
    yAxisFields.forEach((field) => {
      const label = getFieldLabel(field)
      mapping[label] = field
    })
    return mapping
  }, [yAxisFields, getFieldLabel])

  // Determine if we need a right Y-axis
  const hasRightAxis = yAxisFields.some((field) => yAxisAssignment[field] === 'right')

  // Get fields for left and right axes for labels
  const leftAxisFields = yAxisFields.filter((f) => (yAxisAssignment[f] || 'left') === 'left')
  const rightAxisFields = yAxisFields.filter((f) => yAxisAssignment[f] === 'right')

  // Null handling: Filter out data points where ALL measure values are null
  // This prevents rendering empty bars and makes the chart clearer
  const { chartData, skippedCount } = useMemo(() => {
    if (transformedData.length === 0 || seriesKeys.length === 0) {
      return { chartData: [], skippedCount: 0 }
    }
    const filtered = transformedData.filter(row => {
      // Keep the row if at least one series has a valid numeric value
      return seriesKeys.some(key => isValidNumericValue(row[key]))
    })
    const skipped = transformedData.length - filtered.length
    return { chartData: filtered, skippedCount: skipped }
  }, [transformedData, seriesKeys])

  // Now handle early returns AFTER all hooks
  try {
    if (!data || data.length === 0) {
      return (
        <div className="flex items-center justify-center w-full text-dc-text-muted" style={{ height }}>
          <div className="text-center">
            <div className="text-sm font-semibold mb-1">No data available</div>
            <div className="text-xs text-dc-text-secondary">No data points to display in bar chart</div>
          </div>
        </div>
      )
    }

    if (configError) {
      return (
        <div className="flex items-center justify-center w-full text-dc-warning" style={{ height }}>
          <div className="text-center">
            <div className="text-sm font-semibold mb-1">Configuration Error</div>
            <div className="text-xs">{configError}</div>
          </div>
        </div>
      )
    }

    // Determine stack offset for percentage stacking
    // Disable stacking when dual Y-axis is used (bars on different axes can't be stacked)
    const effectiveShouldStack = shouldStack && !hasRightAxis
    const effectiveIsPercentStack = isPercentStack && !hasRightAxis
    const stackOffset = effectiveIsPercentStack ? 'expand' as const : undefined

    // Check if we should use positive/negative coloring
    // This is enabled when we have single series data with mixed positive/negative values
    const usePositiveNegativeColoring = seriesKeys.length === 1 && chartData.some(row => {
      const value = row[seriesKeys[0]]
      return typeof value === 'number' && value < 0
    })

    // Determine if legend will be shown
    const showLegend = safeDisplayConfig.showLegend

    // Use custom chart margins with extra space for Y-axis labels
    const chartMargins = {
      ...CHART_MARGINS,
      left: 40, // Space for left Y-axis label
      right: hasRightAxis ? 40 : 20 // Extra space for right Y-axis label if needed
    }
    
    // Process target values and add to chart data
    const targetValues = parseTargetValues(displayConfig?.target || '')
    const spreadTargets = spreadTargetValues(targetValues, chartData.length)
    
    // Add target data to chart data if targets exist
    let enhancedChartData = chartData
    if (spreadTargets.length > 0) {
      enhancedChartData = chartData.map((dataPoint, index) => ({
        ...dataPoint,
        __target: spreadTargets[index] || null
      }))
    }
    
    // Validate transformed data
    if (!chartData || chartData.length === 0) {
      return (
        <div className="flex items-center justify-center w-full text-dc-text-muted" style={{ height }}>
          <div className="text-center">
            <div className="text-sm font-semibold mb-1">No valid data</div>
            <div className="text-xs text-dc-text-secondary">No valid data points for bar chart after transformation</div>
          </div>
        </div>
      )
    }

    return (
      <div className="relative w-full" style={{ height }}>
        <ChartContainer height={skippedCount > 0 ? `calc(100% - 20px)` : "100%"}>
          <ComposedChart data={enhancedChartData} margin={chartMargins} stackOffset={stackOffset}>
          {safeDisplayConfig.showGrid && (
            <CartesianGrid strokeDasharray="3 3" />
          )}
          <XAxis
            dataKey="name"
            tick={{ fontSize: 12 }}
            angle={-45}
            textAnchor="end"
            height={60}
          />
          <YAxis
            yAxisId="left"
            orientation="left"
            tick={{ fontSize: 12 }}
            tickFormatter={
              effectiveIsPercentStack
                ? (v) => `${(v * 100).toFixed(0)}%`
                : leftYAxisFormat
                  ? (value) => formatAxisValue(value, leftYAxisFormat)
                  : undefined
            }
            domain={effectiveIsPercentStack ? [0, 1] : undefined}
            label={
              effectiveIsPercentStack
                ? undefined
                : leftAxisFields.length > 0
                  ? {
                      value: leftYAxisFormat?.label || getFieldLabel(leftAxisFields[0]),
                      angle: -90,
                      position: 'left',
                      style: { textAnchor: 'middle', fontSize: '12px' }
                    }
                  : undefined
            }
          />
          {hasRightAxis && (
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fontSize: 12 }}
              tickFormatter={rightYAxisFormat ? (value) => formatAxisValue(value, rightYAxisFormat) : undefined}
              label={
                rightAxisFields.length > 0
                  ? {
                      value: rightYAxisFormat?.label || getFieldLabel(rightAxisFields[0]),
                      angle: 90,
                      position: 'right',
                      style: { textAnchor: 'middle', fontSize: '12px' }
                    }
                  : undefined
              }
            />
          )}
          {safeDisplayConfig.showTooltip && (
            <ChartTooltip
              formatter={(value: any, name: any) => {
                // Handle null values in tooltip
                if (value === null || value === undefined) {
                  return ['No data', name]
                }
                if (name === 'Target') {
                  // Use left Y-axis format for target values
                  return [formatAxisValue(value, leftYAxisFormat), 'Target Value']
                }
                // Format as percentage when using percent stacking
                if (effectiveIsPercentStack && typeof value === 'number') {
                  return [`${(value * 100).toFixed(1)}%`, name]
                }
                // Determine which axis format to use based on series name
                const originalField = seriesKeyToField[name]
                const axisId = originalField && yAxisAssignment[originalField] === 'right' ? 'right' : 'left'
                const formatConfig = axisId === 'right' ? rightYAxisFormat : leftYAxisFormat
                return [formatAxisValue(value, formatConfig), name]
              }}
            />
          )}
          {showLegend && (
            <Legend 
              wrapperStyle={{ fontSize: '12px', paddingTop: '25px' }}
              iconType="rect"
              iconSize={8}
              layout="horizontal"
              align="center"
              verticalAlign="bottom"
              onMouseEnter={(o) => setHoveredLegend(String(o.dataKey || ''))}
              onMouseLeave={() => setHoveredLegend(null)}
            />
          )}
          {seriesKeys.map((seriesKey, index) => {
            // Look up the original field name to get its axis assignment
            const originalField = seriesKeyToField[seriesKey]
            const axisId = originalField && yAxisAssignment[originalField] === 'right' ? 'right' : 'left'
            return (
              <Bar
                key={seriesKey}
                dataKey={seriesKey}
                yAxisId={axisId}
                stackId={effectiveShouldStack ? 'stack' : undefined}
                fill={
                  usePositiveNegativeColoring
                    ? POSITIVE_COLOR
                    : (colorPalette?.colors && colorPalette.colors[index % colorPalette.colors.length]) ||
                      CHART_COLORS[index % CHART_COLORS.length]
                }
                fillOpacity={hoveredLegend ? (hoveredLegend === seriesKey ? 1 : 0.3) : 1}
              >
                {usePositiveNegativeColoring &&
                  chartData.map((entry, entryIndex) => {
                    const value = entry[seriesKey]
                    const fillColor = typeof value === 'number' && value < 0 ? NEGATIVE_COLOR : POSITIVE_COLOR
                    return (
                      <Cell
                        key={`cell-${entryIndex}`}
                        fill={fillColor}
                        fillOpacity={hoveredLegend ? (hoveredLegend === seriesKey ? 1 : 0.3) : 1}
                      />
                    )
                  })}
              </Bar>
            )
          })}
          {spreadTargets.length > 0 && (
            <>
              {/* White background line */}
              <Line
                type="monotone"
                dataKey="__target"
                yAxisId="left"
                stroke="#ffffff"
                strokeWidth={2}
                dot={false}
                activeDot={false}
                connectNulls={false}
              />
              {/* Grey dashed line on top */}
              <Line
                type="monotone"
                dataKey="__target"
                yAxisId="left"
                name="Target"
                stroke="#8B5CF6"
                strokeWidth={2}
                strokeDasharray="2 3"
                dot={false}
                activeDot={false}
                connectNulls={false}
              />
            </>
          )}
          </ComposedChart>
        </ChartContainer>
        {skippedCount > 0 && (
          <div className="text-xs text-dc-text-muted text-center mt-1">
            {skippedCount} data point{skippedCount !== 1 ? 's' : ''} with no values hidden
          </div>
        )}
      </div>
    )
  } catch (error) {
    // 'BarChart rendering error
    return (
      <div className="flex flex-col items-center justify-center w-full text-dc-error p-4" style={{ height }}>
        <div className="text-center">
          <div className="text-sm font-semibold mb-1">Bar Chart Error</div>
          <div className="text-xs mb-2">{error instanceof Error ? error.message : 'Unknown rendering error'}</div>
          <div className="text-xs text-dc-text-muted">Check the data and configuration</div>
        </div>
      </div>
    )
  }
})

export default BarChart